import { create } from 'zustand';
// THE calendar cache rule, not a copy of it. Lives in news-service because
// that is where the cache it describes originates; `@news` aliases
// news-service/src (vite.config.ts / vitest.config.ts / tsconfig.json).
// #375 happened because this rule was written out by hand four times.
import { trimCalendarCache } from '@news/calendar';

// Time range filter options
export type CalendarTimeFilter = 'tomorrow' | 'week';

// Types for news and calendar events
export interface NewsHeadline {
  id: string;
  headline: string;
  timestamp: number;
  source?: string;
  importance?: 'high' | 'medium' | 'low';
  category?: string;
  url?: string;
}

export interface CalendarEvent {
  id: string;
  event: string;
  country: string;
  countryCode?: string;
  time: string;
  timestamp: number;
  importance: 'high' | 'medium' | 'low';
  actual?: string;
  forecast?: string;
  previous?: string;
  unit?: string;
  isReleased?: boolean;
  // 'earnings' for Nasdaq earnings events; undefined/'economic' for the
  // existing economic calendar events.
  category?: 'economic' | 'earnings';
  // Ticker symbol, populated for earnings events.
  ticker?: string;
}

interface NewsState {
  // Connection status
  connectionStatus: 'disconnected' | 'connecting' | 'connected';

  // News headlines (raw data)
  headlines: NewsHeadline[];
  maxHeadlines: number;

  // Calendar events (raw data)
  calendarEvents: CalendarEvent[];
  maxCalendarEvents: number;

  // UI state
  isOverlayVisible: boolean;
  activeTab: 'news' | 'calendar';

  // Filters
  calendarTimeFilter: CalendarTimeFilter;
  calendarImportanceFilter: ('high' | 'medium' | 'low')[];
  calendarCountryFilter: string[];

  // Actions
  setConnectionStatus: (status: 'disconnected' | 'connecting' | 'connected') => void;

  // Headlines actions
  addHeadline: (headline: NewsHeadline) => void;
  setHeadlines: (headlines: NewsHeadline[]) => void;
  clearHeadlines: () => void;

  // Calendar actions
  addCalendarEvent: (event: CalendarEvent) => void;
  updateCalendarEvent: (event: CalendarEvent) => void;
  setCalendarEvents: (events: CalendarEvent[]) => void;
  clearCalendarEvents: () => void;

  // UI actions
  setOverlayVisible: (visible: boolean) => void;
  toggleOverlay: () => void;
  setActiveTab: (tab: 'news' | 'calendar') => void;

  // Filter actions
  setCalendarTimeFilter: (filter: CalendarTimeFilter) => void;
  setCalendarImportanceFilter: (filter: ('high' | 'medium' | 'low')[]) => void;
  setCalendarCountryFilter: (filter: string[]) => void;
  toggleCountryFilter: (country: string) => void;
  toggleImportanceFilter: (importance: 'high' | 'medium' | 'low') => void;

  // Initialize from backend
  initFromBackend: (data: { headlines: NewsHeadline[]; calendarEvents: CalendarEvent[] }) => void;
}

// Helper to normalize headline text for deduplication
const normalizeHeadline = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/\d{1,2}:\d{2}\s*(am|pm)?\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)?\s*\d{0,2}/gi, '') // Remove timestamps
    .replace(/[^\w\s]/g, '') // Remove special chars
    .trim()
    .substring(0, 100); // First 100 chars for comparison
};

// Helper to check if headline is duplicate
const isDuplicateHeadline = (newHeadline: NewsHeadline, existing: NewsHeadline[]): boolean => {
  const normalizedNew = normalizeHeadline(newHeadline.headline);
  return existing.some(h => {
    // Same ID = duplicate
    if (h.id === newHeadline.id) return true;
    // Exact same normalized text = duplicate
    const normalizedExisting = normalizeHeadline(h.headline);
    if (normalizedNew === normalizedExisting) return true;
    return false;
  });
};

// Financial Juice's economic calendar already includes earnings rows (e.g.
// "Micron Q3 2026 Earnings"), but marks them low-importance so they're filtered
// out by the default high-only importance filter. Recognize them, bump importance
// so they surface, and badge index-mover names with their ticker.
const EARNINGS_TICKERS: Array<[RegExp, string]> = [
  [/\bnvidia\b/i, 'NVDA'], [/\bapple\b/i, 'AAPL'], [/\bmicrosoft\b/i, 'MSFT'],
  [/\bamazon\b/i, 'AMZN'], [/\b(alphabet|google)\b/i, 'GOOGL'], [/\bmeta\b/i, 'META'],
  [/\btesla\b/i, 'TSLA'], [/\bbroadcom\b/i, 'AVGO'], [/\bmicron\b/i, 'MU'],
  [/\bnetflix\b/i, 'NFLX'], [/\bfedex\b/i, 'FDX'], [/\b(advanced micro|amd)\b/i, 'AMD'],
  [/\bnike\b/i, 'NKE'], [/\boracle\b/i, 'ORCL'], [/\bsalesforce\b/i, 'CRM'],
  [/\bcostco\b/i, 'COST'], [/\bjpmorgan\b/i, 'JPM'], [/\bqualcomm\b/i, 'QCOM'],
  [/\bintel\b/i, 'INTC'], [/\badobe\b/i, 'ADBE'], [/\bpalantir\b/i, 'PLTR'],
  [/\bcisco\b/i, 'CSCO'], [/\baccenture\b/i, 'ACN'], [/\bpaychex\b/i, 'PAYX'],
];

function classifyCalendarEvent(e: CalendarEvent): CalendarEvent {
  if (e.category === 'earnings') return e; // idempotent
  if (!/\bearnings\b/i.test(e.event || '')) return e;
  let ticker: string | undefined = e.ticker;
  for (const [re, t] of EARNINGS_TICKERS) {
    if (re.test(e.event)) { ticker = t; break; }
  }
  // Bump to high so earnings pass the default 'high'-only importance filter.
  return { ...e, category: 'earnings', importance: 'high', ticker };
}

/**
 * Keep the calendar cache ascending, then hand the cap to the shared rule.
 *
 * Order is load-bearing for consumers (useNextEventIndex auto-scrolls to the
 * first future row), and trimCalendarCache needs a stable input to reason about.
 */
function sortAndTrim(events: CalendarEvent[], max: number): CalendarEvent[] {
  const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
  return trimCalendarCache(sorted, max);
}

export const useNewsStore = create<NewsState>((set, get) => ({
  // Initial state
  connectionStatus: 'disconnected',
  headlines: [],
  maxHeadlines: 100, // Store up to 100, overlay displays 60
  calendarEvents: [],
  // Cap on the cached calendar. Trimming is delegated to trimCalendarCache,
  // which only bites above this count and then drops the FAR END. The old
  // `.sort(asc).slice(0, max)` did the opposite: it kept the oldest events
  // and dropped today's, which is #375 recreated client-side (#377).
  maxCalendarEvents: 100,
  isOverlayVisible: false, // Default hidden until preferences sync
  activeTab: 'news',
  calendarTimeFilter: 'tomorrow', // Default: till tomorrow
  calendarImportanceFilter: ['high'], // Default: high importance only
  calendarCountryFilter: [], // Empty = show all

  // Connection status
  setConnectionStatus: (status) => set({ connectionStatus: status }),

  // Headlines actions
  addHeadline: (headline) => set((state) => {
    // Check for duplicate using both ID and text similarity
    if (isDuplicateHeadline(headline, state.headlines)) {
      return state;
    }

    // Add to front, maintain max size
    const newHeadlines = [headline, ...state.headlines].slice(0, state.maxHeadlines);
    return { headlines: newHeadlines };
  }),

  setHeadlines: (headlines) => {
    // Deduplicate on initial set
    const unique: NewsHeadline[] = [];
    for (const h of headlines) {
      if (!isDuplicateHeadline(h, unique)) {
        unique.push(h);
      }
    }
    // Sorted here rather than trusted from the wire: the head-slice below is
    // only correct on a DESCENDING list, and an ascending one would keep the
    // oldest headlines — the calendar's bug in the other list (#377).
    unique.sort((a, b) => b.timestamp - a.timestamp);
    set({ headlines: unique.slice(0, get().maxHeadlines) });
  },

  clearHeadlines: () => set({ headlines: [] }),

  // Calendar actions
  addCalendarEvent: (rawEvent) => set((state) => {
    const event = classifyCalendarEvent(rawEvent);
    // Check for duplicate by ID
    if (state.calendarEvents.some(e => e.id === event.id)) {
      return state;
    }

    return { calendarEvents: sortAndTrim([...state.calendarEvents, event], state.maxCalendarEvents) };
  }),

  updateCalendarEvent: (rawEvent) => set((state) => {
    const event = classifyCalendarEvent(rawEvent);
    const index = state.calendarEvents.findIndex(e => e.id === event.id);
    if (index === -1) {
      // Not found, add it. This is the live path: `news:calendar` deltas arrive
      // here (WebSocketService), not through addCalendarEvent, so this is where
      // a long session accumulates past the cap.
      return { calendarEvents: sortAndTrim([...state.calendarEvents, event], state.maxCalendarEvents) };
    }

    // Update existing. Re-sorted because a correction can move the timestamp,
    // and consumers read position, not just membership (useNextEventIndex scrolls
    // to the first future row).
    const newEvents = [...state.calendarEvents];
    newEvents[index] = event;
    return { calendarEvents: sortAndTrim(newEvents, state.maxCalendarEvents) };
  }),

  setCalendarEvents: (rawEvents) => {
    const events = rawEvents.map(classifyCalendarEvent);
    // Sort by timestamp and deduplicate
    const unique = events.reduce((acc, e) => {
      if (!acc.some(existing => existing.id === e.id)) {
        acc.push(e);
      }
      return acc;
    }, [] as CalendarEvent[]);

    set({ calendarEvents: sortAndTrim(unique, get().maxCalendarEvents) });
  },

  clearCalendarEvents: () => set({ calendarEvents: [] }),

  // UI actions
  setOverlayVisible: (visible) => set({ isOverlayVisible: visible }),

  toggleOverlay: () => set((state) => ({ isOverlayVisible: !state.isOverlayVisible })),

  setActiveTab: (tab) => set({ activeTab: tab }),

  // Filter actions
  setCalendarTimeFilter: (filter) => set({ calendarTimeFilter: filter }),

  setCalendarImportanceFilter: (filter) => set({ calendarImportanceFilter: filter }),

  setCalendarCountryFilter: (filter) => set({ calendarCountryFilter: filter }),

  toggleCountryFilter: (country) => set((state) => {
    const current = state.calendarCountryFilter;
    if (current.includes(country)) {
      return { calendarCountryFilter: current.filter(c => c !== country) };
    }
    return { calendarCountryFilter: [...current, country] };
  }),

  toggleImportanceFilter: (importance) => set((state) => {
    const current = state.calendarImportanceFilter;
    if (current.includes(importance)) {
      // Don't allow removing all filters
      if (current.length === 1) return state;
      return { calendarImportanceFilter: current.filter(i => i !== importance) };
    }
    return { calendarImportanceFilter: [...current, importance] };
  }),

  // Initialize from backend
  initFromBackend: (data) => {
    const { setHeadlines, setCalendarEvents } = get();
    setHeadlines(data.headlines || []);
    setCalendarEvents(data.calendarEvents || []);
  }
}));

// Get date bounds based on time filter
const getDateBounds = (timeFilter: CalendarTimeFilter) => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;

  let endTime: number;
  switch (timeFilter) {
    case 'tomorrow':
      endTime = todayStart + 2 * dayMs; // End of tomorrow
      break;
    case 'week':
      endTime = todayStart + 7 * dayMs; // End of 7 days from today
      break;
    default:
      endTime = todayStart + 2 * dayMs;
  }

  return { todayStart, endTime, now: now.getTime() };
};

// Selector: Get recent headlines for ticker (limited to 10)
export const useRecentHeadlines = () => {
  const headlines = useNewsStore((state) => state.headlines);
  return headlines.slice(0, 10);
};

// Selector: Get headlines for overlay panel (limited to 60)
export const useHeadlinesForOverlay = () => {
  const headlines = useNewsStore((state) => state.headlines);
  return headlines.slice(0, 60);
};

// Selector: Get filtered calendar events based on time, importance, and country filters
export const useFilteredCalendarEvents = () => {
  const events = useNewsStore((state) => state.calendarEvents);
  const timeFilter = useNewsStore((state) => state.calendarTimeFilter);
  const importanceFilter = useNewsStore((state) => state.calendarImportanceFilter);
  const countryFilter = useNewsStore((state) => state.calendarCountryFilter);

  const { todayStart, endTime } = getDateBounds(timeFilter);

  return events.filter(e => {
    // Filter by time range
    if (e.timestamp < todayStart || e.timestamp >= endTime) {
      return false;
    }

    // Filter by importance
    if (!importanceFilter.includes(e.importance)) {
      return false;
    }

    // Filter by country (empty = show all)
    if (countryFilter.length > 0 && !countryFilter.includes(e.country)) {
      return false;
    }

    return true;
  });
};

// Selector: Get unique countries from calendar events
export const useAvailableCountries = () => {
  const events = useNewsStore((state) => state.calendarEvents);
  const countries = new Set<string>();

  events.forEach(e => {
    if (e.country && e.country.trim()) {
      countries.add(e.country);
    }
  });

  return Array.from(countries).sort();
};

// Selector: Get next upcoming event index for auto-scroll
export const useNextEventIndex = () => {
  const events = useFilteredCalendarEvents();
  const now = Date.now();

  // Find first event that hasn't passed
  const index = events.findIndex(e => e.timestamp > now);
  return index >= 0 ? index : 0;
};

// Selector: Check if event is in the past
export const isEventPast = (event: CalendarEvent): boolean => {
  return event.timestamp < Date.now();
};

// Get day boundaries in Pacific Time (PT) - user's local timezone
const getDayBoundaries = () => {
  const now = new Date();
  // Format current time in PT to get PT's "today"
  const ptFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const ptParts = ptFormatter.formatToParts(now);
  const ptYear = parseInt(ptParts.find(p => p.type === 'year')?.value || '0');
  const ptMonth = parseInt(ptParts.find(p => p.type === 'month')?.value || '0') - 1;
  const ptDay = parseInt(ptParts.find(p => p.type === 'day')?.value || '0');

  // Adjust for DST - check if PT is currently in DST
  const isDST = now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' }).includes('PDT');
  const utcOffset = isDST ? 7 : 8; // PDT = UTC-7, PST = UTC-8
  const todayStart = Date.UTC(ptYear, ptMonth, ptDay, utcOffset, 0, 0);

  const dayMs = 24 * 60 * 60 * 1000;
  return {
    todayStart,
    tomorrowStart: todayStart + dayMs,
    dayAfterStart: todayStart + 2 * dayMs,
  };
};

// Selector: Check if event is today (timestamp between today 00:00 and tomorrow 00:00)
export const isEventToday = (event: CalendarEvent): boolean => {
  const { todayStart, tomorrowStart } = getDayBoundaries();
  return event.timestamp >= todayStart && event.timestamp < tomorrowStart;
};

// Selector: Check if event is tomorrow (timestamp between tomorrow 00:00 and day-after 00:00)
export const isEventTomorrow = (event: CalendarEvent): boolean => {
  const { tomorrowStart, dayAfterStart } = getDayBoundaries();
  return event.timestamp >= tomorrowStart && event.timestamp < dayAfterStart;
};

// Selector: High importance upcoming events (for notifications)
export const useUpcomingHighImportanceEvents = () => {
  const events = useNewsStore((state) => state.calendarEvents);
  const now = Date.now();
  const oneHour = 60 * 60 * 1000;

  return events.filter(e =>
    e.importance === 'high' &&
    e.timestamp > now &&
    e.timestamp < now + oneHour
  );
};

// ============================================================================
// CHART MARKER SELECTORS
// These use the same filters as the news overlay
// ============================================================================

// Selector: Get breaking news headlines for chart markers (lightning bolts)
// Returns headlines from today that are breaking news (high importance or BREAKING category)
export const useBreakingNewsForChart = () => {
  const headlines = useNewsStore((state) => state.headlines);
  const now = Date.now();
  const threeDaysAgo = now - 3 * 24 * 60 * 60 * 1000;

  return headlines.filter(h => {
    // Show headlines within chart's data window (3 days)
    if (h.timestamp < threeDaysAgo) return false;

    // Breaking news: high importance or BREAKING category
    return h.importance === 'high' || h.category === 'BREAKING';
  });
};

// Selector: Get filtered calendar events for chart vertical lines
// Uses the same filters as the overlay (importance, country, time)
export const useCalendarEventsForChart = () => {
  const events = useNewsStore((state) => state.calendarEvents);
  const importanceFilter = useNewsStore((state) => state.calendarImportanceFilter);
  const countryFilter = useNewsStore((state) => state.calendarCountryFilter);

  // For chart, show events within a 48-hour window centered around now
  const now = Date.now();
  const windowStart = now - 12 * 60 * 60 * 1000; // 12 hours ago
  const windowEnd = now + 36 * 60 * 60 * 1000; // 36 hours from now

  return events.filter(e => {
    // Time range: 12 hours ago to 36 hours from now
    if (e.timestamp < windowStart || e.timestamp >= windowEnd) {
      return false;
    }

    // Filter by importance (same as overlay)
    if (!importanceFilter.includes(e.importance)) {
      return false;
    }

    // Filter by country (empty = show all, same as overlay)
    if (countryFilter.length > 0 && !countryFilter.includes(e.country)) {
      return false;
    }

    return true;
  });
};
